/**
 * compose — every derived figure the Pulse overview shows, as pure functions
 * over the payload the dashboard already loads.
 *
 * Nothing here calls an API and nothing here is a model. Same policy as
 * useClassicOverview's composition layer: a panel may only state what the
 * payload proves, so the arithmetic lives in one testable place
 * (`compose.check.js` runs it under node with no browser).
 *
 * Source fields, all from GET /api/v1/classic-dashboard/overview:
 *   totals    { trackedMinutes, activeMinutes, idleMinutes, prevTrackedMinutes }
 *   daySeries [{ dateLocal, label, activeMinutes, idleMinutes, prevTotalMinutes }]
 *   weekMatrix  matrix[dayIndex 0=Mon..6=Sun][hour 0..23] = active minutes
 *   hourCurve   [24] active minutes across the whole window
 *   people    { total, tracked, silent[] }
 *   projects  [{ projectId, name, minutes, prevMinutes }]
 *   detail    { coverage:{covered,total}, switches:{…}|null, flow:{…}|null }
 * plus kpi/goals from the parity reads and teamRows from team-analytics.
 */

export const H = (mins) => Math.round((Number(mins) || 0) / 60);
export const pctOf = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

/* ── org health ─────────────────────────────────────────────────────────
   Four pillars, each a percentage already meaningful on its own, averaged
   into one score. A pillar whose input is missing is DROPPED rather than
   scored zero — an org with no goals module must not read as failing. */
export function buildHealth(overview, goals) {
  const pillars = [];
  const t = overview?.totals;
  const people = overview?.people;
  const projects = overview?.projects || [];

  if (people && people.total > 0) {
    pillars.push({
      key: "Engagement",
      value: Math.min(100, pctOf(people.tracked, people.total)),
      why: `${people.tracked} of ${people.total} people tracked time this period`,
    });
  }
  if (t && t.trackedMinutes > 0) {
    pillars.push({
      key: "Focus",
      value: Math.min(100, pctOf(t.activeMinutes, t.trackedMinutes)),
      why: `${pctOf(t.activeMinutes, t.trackedMinutes)}% of tracked time was active, against an 80% target`,
    });
  }
  /* Balance scores how far the largest project sits above an even split.
     With one project there is no split to be uneven, so the pillar is
     meaningless and is left out rather than scored 0. */
  if (projects.length > 1 && t && t.trackedMinutes > 0) {
    const topShare = projects[0].minutes / t.trackedMinutes;
    const fair = 1 / projects.length;
    const value = Math.max(0, Math.min(100, Math.round(100 * (1 - (topShare - fair) / (1 - fair)))));
    pillars.push({
      key: "Balance",
      value,
      why: `${projects[0].name} holds ${Math.round(topShare * 100)}% of tracked time across ${projects.length} projects`,
    });
  }
  if (goals && goals.totalGoalsCount > 0) {
    pillars.push({
      key: "Delivery",
      value: Math.max(0, Math.min(100, Math.round(goals.teamAverageScore || 0))),
      why: `${goals.completedGoalsCount} of ${goals.totalGoalsCount} goals complete`,
    });
  }

  const score = pillars.length
    ? Math.round(pillars.reduce((a, p) => a + p.value, 0) / pillars.length)
    : null;
  return { score, pillars };
}

/* ── the verdict sentence ───────────────────────────────────────────────
   One claim, and only claims the payload proves. Ordered by what a reader
   should act on first: concentration, then a fall, then a clean rise. */
export function buildVerdict(overview) {
  const t = overview?.totals;
  if (!t || !t.trackedMinutes) return null;

  const projects = overview?.projects || [];
  const people = overview?.people;
  const top = projects[0];
  const topShare = top ? top.minutes / t.trackedMinutes : 0;
  const idlePct = pctOf(t.idleMinutes, t.trackedMinutes);
  const delta = t.prevTrackedMinutes > 0
    ? Math.round(((t.trackedMinutes - t.prevTrackedMinutes) / t.prevTrackedMinutes) * 100)
    : null;
  const silent = people?.silent?.length || 0;

  let lead;
  if (delta === null) lead = `The team tracked ${H(t.trackedMinutes)} hours`;
  else if (delta > 2) lead = `Your team worked harder than last period`;
  else if (delta < -2) lead = `Your team tracked ${Math.abs(delta)}% less than last period`;
  else lead = `Your team held steady on last period`;

  let tail = "";
  if (top && topShare >= 0.35) tail = `but ${top.name} is quietly taking a third of it.`;
  else if (silent > 0) tail = `but ${silent} ${silent === 1 ? "person" : "people"} logged nothing at all.`;
  else if (idlePct >= 25) tail = `but a quarter of that time was idle.`;

  return { lead, tail, delta, topShare: Math.round(topShare * 100), idlePct, silent };
}

/* ── project movement ───────────────────────────────────────────────────
   Hours gained or lost against the previous period, biggest gain first.
   A project with no previous period is a genuine new start, not a +100%. */
export function buildMovement(overview) {
  const projects = overview?.projects || [];
  const rows = projects.map((p, i) => ({
    projectId: p.projectId,
    name: p.name,
    slot: i,
    now: p.minutes,
    was: p.prevMinutes || 0,
    gain: H(p.minutes) - H(p.prevMinutes || 0),
    isNew: !p.prevMinutes,
  }));
  rows.sort((a, b) => b.gain - a.gain);

  const t = overview?.totals;
  const orgGain = t && t.prevTrackedMinutes > 0 ? H(t.trackedMinutes) - H(t.prevTrackedMinutes) : null;
  const leader = rows[0];
  /* The line worth printing: one project growing by more than the whole
     company did means the growth was taken, not added. */
  const tookIt = leader && orgGain !== null && leader.gain > 0 && leader.gain > orgGain
    ? { name: leader.name, gain: leader.gain, orgGain, rest: orgGain - leader.gain }
    : null;

  return { rows, orgGain, tookIt };
}

/* ── squarified treemap (Bruls, Huizing & van Wijk) ─────────────────────
   Areas are exact shares by construction; the algorithm only chooses HOW
   the rectangle is cut, favouring near-square tiles. Handles any count, so
   it holds up for an org with 1 project and one with 12.
   Returns fractions of the container: {index, x, y, w, h} in 0..1. */
export function squarify(values, width = 1, height = 1) {
  const total = values.reduce((a, b) => a + b, 0);
  if (!total || !values.length) return [];
  const items = values
    .map((v, index) => ({ index, v: (v / total) * width * height }))
    .filter((it) => it.v > 0)
    .sort((a, b) => b.v - a.v);

  const out = [];
  let x = 0, y = 0, w = width, h = height;

  const worst = (row, side) => {
    const s = row.reduce((a, b) => a + b.v, 0);
    if (!s || !side) return Infinity;
    let mx = -Infinity, mn = Infinity;
    row.forEach((r) => { if (r.v > mx) mx = r.v; if (r.v < mn) mn = r.v; });
    return Math.max((side * side * mx) / (s * s), (s * s) / (side * side * mn));
  };

  let queue = items.slice();
  let guard = 0;
  while (queue.length && guard++ < 1000) {
    const side = Math.min(w, h);
    let row = [];
    let best = Infinity;
    while (queue.length) {
      const candidate = row.concat([queue[0]]);
      const score = worst(candidate, side);
      if (row.length && score > best) break;
      best = score;
      row = candidate;
      queue.shift();
    }
    const sum = row.reduce((a, b) => a + b.v, 0);
    if (w >= h) {
      const rw = h > 0 ? sum / h : 0;
      const colX = x;
      let cy = y;
      row.forEach((it) => {
        const rh = rw > 0 ? it.v / rw : 0;
        out.push({ index: it.index, x: colX, y: cy, w: rw, h: rh });
        cy += rh;
      });
      x += rw; w -= rw;
    } else {
      const rh = w > 0 ? sum / w : 0;
      const rowY = y;
      let cx = x;
      row.forEach((it) => {
        const rw2 = rh > 0 ? it.v / rh : 0;
        out.push({ index: it.index, x: cx, y: rowY, w: rw2, h: rh });
        cx += rw2;
      });
      y += rh; h -= rh;
    }
  }
  return out.sort((a, b) => a.index - b.index);
}

/* Top N projects by time, with the tail folded into one "Other" tile so the
   treemap never degenerates into unreadable slivers. */
export function projectTiles(overview, limit = 8) {
  const projects = overview?.projects || [];
  const total = overview?.totals?.trackedMinutes || 0;
  if (!projects.length || !total) return [];
  const head = projects.slice(0, limit);
  const tail = projects.slice(limit);
  const tiles = head.map((p, i) => ({
    key: String(p.projectId),
    name: p.name,
    minutes: p.minutes,
    prevMinutes: p.prevMinutes || 0,
    share: pctOf(p.minutes, total),
    slot: i,
    drill: { kind: "project", id: p.projectId, name: p.name },
  }));
  if (tail.length) {
    const rest = tail.reduce((a, p) => a + p.minutes, 0);
    if (rest > 0) {
      tiles.push({
        key: "__other",
        name: `${tail.length} more`,
        minutes: rest,
        prevMinutes: tail.reduce((a, p) => a + (p.prevMinutes || 0), 0),
        share: pctOf(rest, total),
        slot: limit,
        drill: null,
      });
    }
  }
  return tiles;
}

/* ── people quadrant ────────────────────────────────────────────────────
   Hours tracked across, active share up. Targets cut it into four named
   zones; the ZONE NAME carries the meaning, never the dot colour. People
   who tracked nothing are excluded and counted separately — plotting them
   at the origin would invent a reading. */
export const QUADRANT_TARGET = { hours: 32, active: 78 };

export function buildQuadrant(teamRows, target = QUADRANT_TARGET, rangeDays = 7) {
  const rows = Array.isArray(teamRows) ? teamRows : [];
  /* The hours target is a full week. Scale it to the window actually being
     viewed, or a one-day range would put the entire team in "low hours". */
  const hoursTarget = Math.max(1, Math.round((target.hours / 7) * Math.max(1, rangeDays)));

  const points = [];
  let untracked = 0;
  rows.forEach((r) => {
    const active = Number(r.activeMinutes) || 0;
    const idle = Number(r.idleMinutes) || 0;
    const totalMins = active + idle;
    if (totalMins <= 0) { untracked += 1; return; }
    const hours = totalMins / 60;
    const share = Math.round((active / totalMins) * 100);
    const zone = hours >= hoursTarget
      ? (share >= target.active ? "performing" : "stretched")
      : (share >= target.active ? "spare" : "check");
    points.push({
      userId: r.userId, name: r.name, position: r.position, avatar: r.avatar,
      hours: Math.round(hours * 10) / 10, share, zone,
    });
  });

  const counts = { performing: 0, spare: 0, stretched: 0, check: 0 };
  points.forEach((p) => { counts[p.zone] += 1; });

  const maxHours = points.reduce((a, p) => Math.max(a, p.hours), 0);
  return {
    points, counts, untracked, hoursTarget,
    activeTarget: target.active,
    // a little headroom either side so no dot sits on the frame
    xMax: Math.max(hoursTarget * 1.5, Math.ceil((maxHours + 2) / 5) * 5),
  };
}

export const ZONES = [
  { key: "performing", label: "Performing", tone: "positive", tip: "At or above both targets — hours and focus." },
  { key: "spare", label: "Capacity to spare", tone: "info", tip: "Focused, but well under a full week. Room to take more on." },
  { key: "stretched", label: "Stretched thin", tone: "warning", tip: "Long hours, low active share — usually meetings and context-switching." },
  { key: "check", label: "Needs a conversation", tone: "negative", tip: "Short hours and low focus together. Worth asking why." },
];

/* ── the working day ────────────────────────────────────────────────────
   Two genuine peaks = two local maxima at least 3h apart with a real dip
   between them. Same test the existing lede uses, kept identical so the
   two surfaces can never disagree about how many peaks the day has. */
export function buildDayShape(hourCurve) {
  const curve = Array.isArray(hourCurve) && hourCurve.length === 24 ? hourCurve : null;
  if (!curve || !curve.some((v) => v > 0)) return null;

  const max = Math.max(...curve);
  const first = curve.indexOf(max);
  let second = null;
  for (let h = 0; h < 24; h += 1) {
    if (Math.abs(h - first) < 3) continue;
    if (curve[h] < max * 0.6) continue;
    const lo = Math.min(h, first), hi = Math.max(h, first);
    const dip = Math.min(...curve.slice(lo, hi + 1));
    if (dip <= curve[h] * 0.75 && (second === null || curve[h] > curve[second])) second = h;
  }

  const totalActive = curve.reduce((a, b) => a + b, 0);
  const core = curve.slice(9, 18).reduce((a, b) => a + b, 0);
  const evening = curve.slice(18).reduce((a, b) => a + b, 0);

  // the quietest hour that still sits inside the working day
  let dipHour = null;
  const lo = Math.min(first, second === null ? first : second);
  const hi = Math.max(first, second === null ? first : second);
  if (second !== null) {
    let lowest = Infinity;
    for (let h = lo + 1; h < hi; h += 1) if (curve[h] < lowest) { lowest = curve[h]; dipHour = h; }
  }

  return {
    curve, max, peak: first, second, dipHour, totalActive,
    corePct: pctOf(core, totalActive),
    eveningPct: pctOf(evening, totalActive),
  };
}

/* The window worth drawing: trim hours that are empty across the whole
   period at BOTH ends, so a 24-column grid is not two-thirds dead space.
   Always keeps at least 09:00–18:00 so the shape stays comparable. */
export function activeHourWindow(hourCurve, pad = 1) {
  const curve = Array.isArray(hourCurve) ? hourCurve : [];
  let from = 24, to = -1;
  curve.forEach((v, h) => { if (v > 0) { if (h < from) from = h; if (h > to) to = h; } });
  if (to < 0) return { from: 6, to: 21, hiddenPct: 0 };
  from = Math.max(0, Math.min(from - pad, 9));
  to = Math.min(23, Math.max(to + pad, 18));
  const total = curve.reduce((a, b) => a + b, 0);
  let shown = 0;
  for (let h = from; h <= to; h += 1) shown += curve[h] || 0;
  return { from, to, hiddenPct: total > 0 ? Math.round(((total - shown) / total) * 1000) / 10 : 0 };
}

/* ── heatmap ────────────────────────────────────────────────────────────
   weekMatrix is already [day][hour] active minutes, so the only work is
   finding the bucket boundaries and the busiest cell. */
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function buildHeat(weekMatrix, steps = 6) {
  const m = Array.isArray(weekMatrix) && weekMatrix.length ? weekMatrix : null;
  if (!m) return null;
  let max = 0, peak = null;
  m.forEach((row, d) => (row || []).forEach((v, h) => {
    if (v > max) { max = v; peak = { day: d, hour: h, minutes: v }; }
  }));
  if (max <= 0) return null;
  const bucket = (v) => (v <= 0 ? 0 : Math.min(steps - 1, 1 + Math.floor((v / max) * (steps - 1))));
  const dayTotals = m.map((row) => (row || []).reduce((a, b) => a + b, 0));
  return { matrix: m, max, peak, bucket, dayTotals, maxDay: Math.max(...dayTotals, 1) };
}
